import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config/api';

/**
 * 🌍 World ticker — the LIVE joins · hatches · published-replay strip.
 *
 * ⭐ ONE implementation, two mounts (/marketplace + /explore, Nina 08-14 eve).
 * It was inline JSX in MarketplacePage; putting a second copy on /explore is
 * exactly the drift that let a DEAD model id keep shipping from four unfixed
 * frontend copies (DEAD_MODEL_SEATS §1b) and that ArtStyle.js was created to
 * end. Extracted instead of copied.
 *
 * DATA: pass `activity` if the host already fetched it (MarketplacePage rides
 * its own /api/agents/marketplace call — no second request). Omit it and the
 * ticker fetches its own, which is what /explore does; it degrades to
 * rendering nothing on failure, never a broken strip.
 *
 * SHORT LISTS RENDER STATIC on purpose: the marquee duplicates its children to
 * loop seamlessly, and with <4 items that duplication reads as a bug
 * ("Claude slid in" ×2, Nina 08-14).
 */
const WorldTicker = ({ activity: activityProp, style }) => {
    const [fetched, setFetched] = useState(null);
    const selfFetch = activityProp === undefined;

    useEffect(() => {
        if (!selfFetch) return;
        let alive = true;
        (async () => {
            try {
                // limit=1: we want the `activity` array, not the agent grid.
                const res = await fetch(`${API_BASE_URL}/api/agents/marketplace?limit=1`);
                const data = await res.json();
                if (alive && data && data.success && Array.isArray(data.activity)) setFetched(data.activity);
            } catch (e) { /* ticker is chrome — never break the page for it */ }
        })();
        return () => { alive = false; };
    }, [selfFetch]);

    const activity = selfFetch ? (fetched || []) : (activityProp || []);
    if (!activity.length) return null;

    const animated = activity.length >= 4;

    const timeAgo = (iso) => {
        if (!iso) return '';
        const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (s < 60) return 'just now';
        const m = Math.floor(s / 60);
        if (m < 60) return `${m}m ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;
        return `${Math.floor(h / 24)}d ago`;
    };

    const renderEvent = (ev, i) => {
        const isHatch = ev.type === 'hatched';
        const isReplay = ev.type === 'replay';
        const href = isHatch ? `/agents/${ev.agent_id}`
            : isReplay ? `/explorer/theater?replay=${encodeURIComponent(ev.session)}`
                : `/game-step?gameId=${ev.game_id}`;
        return (
            <span
                key={i}
                className="marketplace-ticker-item"
                style={styles.tickerItem}
                onClick={() => window.open(href, '_blank')}
                title={isHatch ? 'Meet them' : isReplay ? 'Watch the replay' : 'Jump into this space'}
            >
                {isHatch ? (
                    <>&#128035; <strong>{ev.agent_name}</strong>&nbsp;hatched into the marketplace</>
                ) : isReplay ? (
                    <>&#128252; <strong>{ev.agent_name}</strong>&nbsp;shared a walk replay</>
                ) : (
                    <>&#129302; <strong>{ev.agent_name}</strong>&nbsp;slid into&nbsp;<em>{ev.space_name || 'a space'}</em></>
                )}
                <span style={styles.tickerTime}>&nbsp;{timeAgo(ev.joined_at || ev.ts)}</span>
            </span>
        );
    };

    return (
        <>
            {/* Self-contained CSS so the ticker works on any host page. Class
                names keep the `marketplace-` prefix they shipped with — the
                marketplace style block still defines them too, and renaming
                would silently drop the animation there. */}
            <style>{`
                @keyframes marketplaceTickerScroll {
                  0%   { transform: translateX(0); }
                  100% { transform: translateX(-50%); }
                }
                .marketplace-ticker-track {
                  animation-name: marketplaceTickerScroll;
                  animation-timing-function: linear;
                  animation-iteration-count: infinite;
                }
                .marketplace-ticker-track:hover { animation-play-state: paused; }
                .marketplace-ticker-item:hover { color: #c084fc !important; }
            `}</style>
            <div className="mp-sec-ticker" style={{ ...styles.tickerWrap, ...(style || {}) }}>
                <span style={styles.tickerLive}>&#128308; LIVE</span>
                <div style={styles.tickerViewport}>
                    <div
                        className={animated ? 'marketplace-ticker-track' : undefined}
                        style={{ ...styles.tickerTrack, ...(animated ? { animationDuration: `${Math.max(activity.length * 5, 20)}s` } : {}) }}
                    >
                        {(animated ? [...activity, ...activity] : activity).map(renderEvent)}
                    </div>
                </div>
            </div>
        </>
    );
};

// Full-width top strip (not a card): square corners, no outer margin,
// background = the marketplace hero gradient's start colour so the seam
// disappears there. /explore is dark too, so it reads the same.
const styles = {
    tickerWrap: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: '#1a1a2e',
        padding: '5px 14px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        overflow: 'hidden',
    },
    tickerLive: {
        fontSize: '0.68rem',
        fontWeight: 800,
        letterSpacing: '0.08em',
        color: '#fff',
        background: 'rgba(239, 68, 68, 0.25)',
        border: '1px solid rgba(239, 68, 68, 0.6)',
        borderRadius: 6,
        padding: '2px 7px',
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    tickerViewport: { overflow: 'hidden', flex: 1, minWidth: 0 },
    tickerTrack: { display: 'inline-flex', whiteSpace: 'nowrap', willChange: 'transform' },
    tickerItem: {
        color: 'rgba(255,255,255,0.85)',
        fontSize: '0.85rem',
        padding: '0 22px 0 0',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    tickerTime: { color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem' },
};

export default WorldTicker;
